import { useEffect, useState } from 'react'
import api from '../../services/api'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import Button from '../../components/common/Button'
import MessageTable from './components/MessageTable'
import MessageDetail from './components/MessageDetail'
import './Messages.css'

interface Message {
  id: number
  name: string
  email: string
  subject: string
  message: string
  is_read: boolean
  is_processed: boolean
  ip_address?: string
  created_at: string
}

function Messages() {
  const [messages, setMessages] = useState<Message[]>([])
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')

  useEffect(() => {
    loadMessages()
  }, [])

  useEffect(() => {
    applyFilter()
  }, [messages, filter])

  const loadMessages = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.get<Message[]>('/messages')
      // Sort by date DESC (newest first)
      const sortedMessages = data.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      setMessages(sortedMessages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des messages')
    } finally {
      setLoading(false)
    }
  }

  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredMessages(messages)
    } else if (filter === 'unread') {
      setFilteredMessages(messages.filter(m => !m.is_read))
    } else {
      setFilteredMessages(messages.filter(m => m.is_read))
    }
  }

  const handleViewMessage = async (message: Message) => {
    setSelectedMessage(message)

    // Auto-mark as read when opening if not already read
    if (!message.is_read) {
      try {
        await api.put(`/messages/${message.id}/read`, {})
        // Update local state
        setMessages(messages.map(m =>
          m.id === message.id ? { ...m, is_read: true } : m
        ))
      } catch (err) {
        // Log only in development
        if (import.meta.env.DEV) {
          console.error('Failed to mark message as read:', err)
        }
        // Silent failure when auto-marking as read (user still sees the message)
      }
    }
  }

  const handleMarkAsRead = async (messageId: number) => {
    try {
      await api.put(`/messages/${messageId}/read`, {})
      setMessages(messages.map(m =>
        m.id === messageId ? { ...m, is_read: true } : m
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du marquage comme lu')
    }
  }

  const handleDeleteMessage = async (messageId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.')) {
      return
    }

    try {
      await api.delete(`/messages/${messageId}`)
      setMessages(messages.filter(m => m.id !== messageId))
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression du message')
    }
  }

  const unreadCount = messages.filter(m => !m.is_read).length

  if (loading) {
    return <Loading />
  }

  return (
    <div className="messages-container">
      <div className="messages-header">
        <div>
          <h1>Messages de contact</h1>
          <p className="messages-subtitle">
            {messages.length} message{messages.length > 1 ? 's' : ''} au total
            {unreadCount > 0 && (
              <span className="unread-badge"> · {unreadCount} non lu{unreadCount > 1 ? 's' : ''}</span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={loadMessages}>
          Actualiser
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="messages-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Tous ({messages.length})
        </button>
        <button
          className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Non lus ({messages.filter(m => !m.is_read).length})
        </button>
        <button
          className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Lus ({messages.filter(m => m.is_read).length})
        </button>
      </div>

      <MessageTable
        messages={filteredMessages}
        onView={handleViewMessage}
        onMarkAsRead={handleMarkAsRead}
        onDelete={handleDeleteMessage}
      />

      {selectedMessage && (
        <MessageDetail
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onDelete={handleDeleteMessage}
        />
      )}
    </div>
  )
}

export default Messages
